import React, { useEffect, useContext, useCallback } from 'react';
import { QUESTIONNAIRES_QUERY } from '../../queries';
import { QUESTIONNAIRE_DELETE_SUBSCRIPTION } from '../../subscriptions';
import { ServiceContext } from 'providers/ServiceProvider';
import ExportAction from './actions/ExportAction';
import { useTranslation } from 'react-i18next';
import { Card } from '@totalsoft/rocket-ui';
import { useApolloClient } from '@apollo/client';
import { getListBrowserStore, useListBrowserUtils } from '@ts-private/list-browser';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { useToast } from 'hooks/toasts/useToast';
import { useMemo } from 'react';
import ApolloFetchingComponent from 'features/common/components/apollo-fetching/ApolloFetchingComponent';
import TableListBrowser from 'features/common/components/table-list-browser/TableListBrowser';
import QuestionnaireItem from './QuestionnaireItem';
import { useMutation } from 'hooks/apollo/useMutation';
import { QUESTIONNAIRE_DELETE } from 'features/questionnaires/mutations';
import useConfirmDialog from 'features/common/components/confirm/useConfirmDialog';
import useSubscription from 'hooks/apollo/useSubscription';

export const questionnairesStore = getListBrowserStore({
  filterDefinitions: [
    {
      name: 'name',
      caption: 'Questionnaire.Name',
      type: 'text'
    },
    {
      name: 'code',
      caption: 'Questionnaire.Code',
      type: 'text'
    }
  ],
  orderByFields: [
    {
      caption: 'Questionnaire.Name',
      value: 'Name'
    }
  ],
  pager: {
    orderBy: 'Name',
    direction: 'asc'
  }
});

const QuestionnaireListBrowser = () => {
  const [t] = useTranslation();
  const addToast = useToast();
  const client = useApolloClient();
  const [activeService] = useContext(ServiceContext);
  const { addActions } = useListBrowserUtils(questionnairesStore);
  const confirm = useConfirmDialog();

  const fetchingOptions = useMemo(
    () => ({
      queryOptions: {
        query: QUESTIONNAIRES_QUERY,
        name: 'questionnaires'
      }
    }),
    []
  );

  // #region Delete

  useSubscription(QUESTIONNAIRE_DELETE_SUBSCRIPTION, {
    onData: data => {
      addToast(t('Questionnaire.QuestionnaireDeleted'), 'success');
      client.cache.evict({ id: `Questionnaire:{"questionnaireId":${data.data.data.questionnaireDeleted.questionnaireId}}` });
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'questionnaires' });
      client.cache.gc();
    }
  });

  const [deleteQuestionaire] = useMutation(QUESTIONNAIRE_DELETE);

  const handleDelete = useCallback(
    questionnaireId => {
      confirm(
        () => {
          deleteQuestionaire({ variables: { questionnaireId } });
        },
        {
          content: t('Questionnaire.DeleteQuestionnaire')
        }
      );
    },
    [deleteQuestionaire, confirm, t]
  );

  // #endregion

  useEffect(() => {
    if (activeService) {
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'questionnaires' });
      client.cache.gc();
    }
  }, [activeService, client]);

  // TODO Wrong way to add actions to list-browser!! Will need a refactor
  useEffect(() => {
    addActions([
      {
        name: 'export',
        icon: <ExportAction />
      }
    ]);
  }, [addActions, t]);

  const itemComponent = useCallback(({ data }) => <QuestionnaireItem data={data} onDelete={handleDelete} />, [handleDelete]);

  const columns = useMemo(
    () => [
      { name: 'name', caption: 'Questionnaire.Name' },
      { name: 'code', caption: 'Questionnaire.Code' },
      { name: 'version', caption: 'Questionnaire.Version', width: 10, align: 'center' },
      { name: 'validFrom', caption: 'Questionnaire.ValidFrom' },
      { name: 'validTo', caption: 'Questionnaire.ValidTo' },
      { name: 'active', caption: 'Questionnaire.Active', width: 10, align: 'center' },
      { name: 'actions', caption: 'Actions', width: 15 }
    ],
    []
  );

  return (
    <Card icon={ListAltIcon}>
      <TableListBrowser name='questionnaire-browser' store={questionnairesStore} ItemComponent={itemComponent} columns={columns} />
      <ApolloFetchingComponent store={questionnairesStore} options={fetchingOptions} />
    </Card>
  );
};

export default QuestionnaireListBrowser;
